import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import API from '../../../services/API'
import ExposureOutcomeChip from '../components/ExposureOutcomeChip'

// Tri-state question: 'yes' | 'no' | 'unsure'. "Not sure" is tracked
// separately (see notSureFlags) rather than as a third boolean value, so the
// server's assessExemption() can force an uncertain outcome whenever any
// decisive question is left unresolved - never guessing toward a confident
// exempt/registrable answer.
const QUESTIONS = [
  {
    key: 'firstAidOnly',
    title: 'First aid only',
    question:
      'Is the medical cover being provided for this event limited to first aid - i.e. no clinical assessment, diagnosis or treatment beyond first-aid level?',
  },
  {
    key: 'employerArrangedForOwnEmployeesOnly',
    title: 'Employer-arranged cover',
    question:
      'Is this cover arranged solely by an employer for their own employees (not for attendees or the public)?',
  },
  {
    key: 'armedForcesProvision',
    title: 'Armed forces',
    question: 'Does this provision fall under the armed forces defence medical/dental services?',
  },
  {
    key: 'medicalPractitionerFixedBaseOnly',
    title: 'Medical practitioner, fixed base',
    question:
      'Is treatment given only by a registered medical practitioner from a fixed base on the event site (i.e. not roaming across the site)?',
    hasSubExclusions: true,
  },
]

const TriStateQuestion = ({ q, value, notSure, onChange }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="subtitle1" fontWeight={700}>
      {q.title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
      {q.question}
    </Typography>
    <RadioGroup
      row
      value={notSure ? 'unsure' : value === true ? 'yes' : value === false ? 'no' : ''}
      onChange={(e) => onChange(e.target.value)}
    >
      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
      <FormControlLabel value="no" control={<Radio />} label="No" />
      <FormControlLabel value="unsure" control={<Radio />} label="Not sure" />
    </RadioGroup>
  </Paper>
)

const ExemptionWizard = ({ eventID, embedded = false, onAssessed }) => {
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [exemptionTypes, setExemptionTypes] = useState([])
  const [disclaimer, setDisclaimer] = useState('')
  const [result, setResult] = useState(null)
  const [answers, setAnswers] = useState({
    firstAidOnly: undefined,
    employerArrangedForOwnEmployeesOnly: undefined,
    armedForcesProvision: undefined,
    medicalPractitionerFixedBaseOnly: undefined,
    medicalPractitionerSubExclusionsPresent: [],
    transportOccursOffVenue: undefined,
    treatmentGivenDuringTransport: undefined,
    notSureFlags: [],
  })

  useEffect(() => {
    if (!eventID) return
    setLoading(true)
    API.get(`/events/${eventID}/cqc-exposure`)
      .then((res) => {
        if (res.status !== 200) return
        setExemptionTypes(res.data.exemptionTypes || [])
        setDisclaimer(res.data.disclaimer || '')
        if (res.data.assessment) {
          setAnswers((prev) => ({ ...prev, ...res.data.assessment.answers }))
          if (res.data.assessment.status === 'completed') {
            setResult({
              outcome: res.data.assessment.outcome,
              applicableExemptionKey: res.data.assessment.applicableExemptionKey,
              rationale: res.data.assessment.rationale,
            })
          }
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }, [eventID])

  const setAnswer = (key, uiValue) => {
    setAnswers((prev) => {
      const notSureFlags = new Set(prev.notSureFlags)
      if (uiValue === 'unsure') {
        notSureFlags.add(key)
      } else {
        notSureFlags.delete(key)
      }
      return {
        ...prev,
        [key]: uiValue === 'yes' ? true : uiValue === 'no' ? false : undefined,
        notSureFlags: Array.from(notSureFlags),
      }
    })
  }

  const toggleSubExclusion = (subKey) => {
    setAnswers((prev) => {
      const set = new Set(prev.medicalPractitionerSubExclusionsPresent || [])
      if (set.has(subKey)) set.delete(subKey)
      else set.add(subKey)
      return { ...prev, medicalPractitionerSubExclusionsPresent: Array.from(set) }
    })
  }

  const submit = () => {
    setSubmitting(true)
    API.post(`/events/${eventID}/cqc-exposure`, { answers })
      .then((res) => {
        if (res.status === 200) {
          setResult({
            outcome: res.data.assessment.outcome,
            applicableExemptionKey: res.data.assessment.applicableExemptionKey,
            rationale: res.data.assessment.rationale,
          })
          onAssessed?.(res.data.assessment)
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setSubmitting(false))
  }

  if (!eventID) return <Alert severity="warning">Save the event before assessing CQC exposure.</Alert>
  if (loading) return <LinearProgress />

  const medPractitionerExemption = exemptionTypes.find(
    (e) => e.key === 'medical_practitioner_consulting_room',
  )
  const currentQuestion = QUESTIONS[activeStep]
  const isTransportStep = activeStep === QUESTIONS.length
  const isReviewStep = activeStep === QUESTIONS.length + 1

  const canAdvance = () => {
    if (isTransportStep) return true
    if (!currentQuestion) return true
    const answered =
      answers[currentQuestion.key] !== undefined || answers.notSureFlags.includes(currentQuestion.key)
    return answered
  }

  return (
    <Stack gap={2}>
      <Alert severity="info" variant="outlined">
        This wizard gives indicative guidance only, based on the answers you provide - it is not a
        legal determination of exempt/registrable status. For borderline cases, or before relying on
        this for a registration decision, take advice from CQC or a specialist regulatory adviser.
      </Alert>

      <Stepper activeStep={activeStep} alternativeLabel={!embedded}>
        {QUESTIONS.map((q) => (
          <Step key={q.key}>
            <StepLabel>{q.title}</StepLabel>
          </Step>
        ))}
        <Step>
          <StepLabel>Transport</StepLabel>
        </Step>
        <Step>
          <StepLabel>Result</StepLabel>
        </Step>
      </Stepper>

      {currentQuestion && (
        <Stack gap={1.5}>
          <TriStateQuestion
            q={currentQuestion}
            value={answers[currentQuestion.key]}
            notSure={answers.notSureFlags.includes(currentQuestion.key)}
            onChange={(v) => setAnswer(currentQuestion.key, v)}
          />
          {currentQuestion.hasSubExclusions && answers.medicalPractitionerFixedBaseOnly && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <FormLabel component="legend" sx={{ mb: 1 }}>
                Does the treatment include any of the following? (tick any that apply - any tick
                here means this exemption won't apply)
              </FormLabel>
              <Stack>
                {(medPractitionerExemption?.subExclusions || []).map((sub) => (
                  <FormControlLabel
                    key={sub.key}
                    control={
                      <Checkbox
                        checked={(answers.medicalPractitionerSubExclusionsPresent || []).includes(
                          sub.key,
                        )}
                        onChange={() => toggleSubExclusion(sub.key)}
                      />
                    }
                    label={sub.label}
                  />
                ))}
              </Stack>
            </Paper>
          )}
        </Stack>
      )}

      {isTransportStep && (
        <Stack gap={1.5}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Transport
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Does any part of the patient's transport occur off the venue confines, or is any
              treatment given during transport?
            </Typography>
            <RadioGroup
              row
              value={
                answers.notSureFlags.includes('transport')
                  ? 'unsure'
                  : answers.transportOccursOffVenue || answers.treatmentGivenDuringTransport
                    ? 'yes'
                    : answers.transportOccursOffVenue === false &&
                        answers.treatmentGivenDuringTransport === false
                      ? 'no'
                      : ''
              }
              onChange={(e) => {
                const v = e.target.value
                setAnswers((prev) => {
                  const notSureFlags = new Set(prev.notSureFlags)
                  if (v === 'unsure') notSureFlags.add('transport')
                  else notSureFlags.delete('transport')
                  return {
                    ...prev,
                    transportOccursOffVenue: v === 'yes' ? true : v === 'no' ? false : undefined,
                    treatmentGivenDuringTransport: v === 'yes' ? true : v === 'no' ? false : undefined,
                    notSureFlags: Array.from(notSureFlags),
                  }
                })
              }}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
              <FormControlLabel value="unsure" control={<Radio />} label="Not sure" />
            </RadioGroup>
          </Paper>
        </Stack>
      )}

      {isReviewStep && (
        <Stack gap={1.5}>
          <Typography variant="body2" color="text.secondary">
            Review your answers using the stepper above if needed, then submit for an assessment.
          </Typography>
          <Button variant="contained" onClick={submit} disabled={submitting}>
            {submitting ? 'Assessing…' : 'Submit assessment'}
          </Button>
          {result && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Outcome:
                </Typography>
                <ExposureOutcomeChip outcome={result.outcome} />
              </Stack>
              <Stack gap={0.5} sx={{ mb: 1 }}>
                {(result.rationale || []).map((r, i) => (
                  <Typography key={i} variant="body2" color="text.secondary">
                    • {r}
                  </Typography>
                ))}
              </Stack>
              <Alert severity="warning" variant="outlined">
                {disclaimer}
              </Alert>
            </Paper>
          )}
        </Stack>
      )}

      <Stack direction="row" justifyContent="space-between">
        <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>
          Back
        </Button>
        {!isReviewStep && (
          <Button
            variant="contained"
            disabled={!canAdvance()}
            onClick={() => setActiveStep((s) => s + 1)}
          >
            Next
          </Button>
        )}
      </Stack>
    </Stack>
  )
}

export default ExemptionWizard
